import type { ImmerGet, ImmerSet } from '../types';

import type {
    BotFormGeneralInfoValues,
    EditableBotVersionSlice,
} from '@/domain/bot';
import type { BotMappieVersion, PromptUpdateParams } from '@/domain/bot-mappie';
import { StickersType } from '@/domain/sticker-module-types';
import { updatePromptInMappieBot } from 'utils/bot/bot-prompt-utils';
import {
    pushUndoAction,
    resetUndoStack,
} from 'utils/bot/state/editable-bot-fns';

/**
 * Creates bot general info action handlers.
 */
export const createBotGeneralInfoActions = (
    set: ImmerSet,
    get: ImmerGet,
): Pick<
    EditableBotVersionSlice,
    | 'setBotGeneralInfo'
    | 'setStickers'
    | 'setPromptTexts'
    | 'setIntentTexts'
    | 'setEntityTexts'
    | 'resetChangesInBotVersion'
    | 'setSelectedHandle'
    | 'setPreviousPath'
    | 'setTestPhoneNumber'
> => ({
    setBotGeneralInfo: (botGeneralInfo: BotFormGeneralInfoValues) => {
        set((state) => {
            pushUndoAction(state, get);
            const bot = state.editingBot;
            bot.name = botGeneralInfo.name;
            bot.description = botGeneralInfo.description;
            bot.channel = botGeneralInfo.channel;
            bot.language = botGeneralInfo.language;
            // bot.labels = botGeneralInfo.labels;
            bot.voiceConfig = botGeneralInfo.voiceConfig
                ? {
                      inactivityTimeout:
                          botGeneralInfo.voiceConfig.inactivityTimeout,
                      initialUserResponseTimeout:
                          botGeneralInfo.voiceConfig.initialUserResponseTimeout,
                      ttsVoice: { ...botGeneralInfo.voiceConfig.ttsVoice },
                      stt: {
                          provider: botGeneralInfo.voiceConfig?.stt?.provider,
                          endOfSpeechTimeout: botGeneralInfo.voiceConfig?.stt
                              ?.endOfSpeechTimeout
                              ? Number(
                                    botGeneralInfo.voiceConfig?.stt
                                        ?.endOfSpeechTimeout,
                                )
                              : undefined,
                      },
                      ttsProsody: { ...botGeneralInfo.voiceConfig.ttsProsody },
                  }
                : undefined;
            state.changesInBotVersion = true;
            bot.mchannelsBotId = botGeneralInfo.mchannelsBotId;
        });
    },

    setStickers: (stickers: StickersType) => {
        set((state) => {
            //@ts-ignore - data type problem
            (state.editingBot.stickers = stickers),
                (state.changesInBotVersion = true);
        });
    },

    setPromptTexts: (value: Record<string, string[]>) => {
        set((state) => {
            state.editingBot.prompt_texts = value;
        });
    },

    setIntentTexts: (value: Record<string, string[]>) => {
        set((state) => {
            state.editingBot.intent_texts = value;
        });
    },

    setEntityTexts: (value: Record<string, string[]>) => {
        set((state) => {
            state.editingBot.entity_texts = value;
        });
    },

    resetChangesInBotVersion: () => {
        set((state) => {
            state.changesInBotVersion = false;
            state.isLoadingBotVersion = false;
            state.errorFetchBotVersion = undefined;

            resetUndoStack(state, get);
        });
    },

    setSelectedHandle: (newSelHandle: string | undefined) => {
        set((state) => {
            if (newSelHandle) {
                const nodeId = newSelHandle.slice(0, -2);
                const updatedNodes = state.editingBot.nodesReactFlow.map(
                    (node) => ({
                        ...node,
                        selected: node.id === nodeId,
                    }),
                );

                return {
                    ...state,
                    editingBot: {
                        ...state.editingBot,
                        selectedHandle: newSelHandle,
                        nodesReactFlow: updatedNodes,
                    },
                    changesInBotVersion: false,
                };
            } else {
                state.editingBot.selectedHandle = newSelHandle;
            }
        });
    },

    setPreviousPath: (path: string) => {
        set((state) => {
            state.previousPath = path;
        });
    },

    setTestPhoneNumber: (number: string) => {
        set((state) => {
            state.testPhoneNumber = number;
        });
    },
});

/**
 * Creates mappie bot action handlers.
 */
export const createMappieBotActions = (
    set: ImmerSet,
    get: ImmerGet,
): Pick<
    EditableBotVersionSlice,
    'setBotMappie' | 'updatePrompt' | 'setGlobalPromptUpdated'
> => ({
    setBotMappie: (botMappie: BotMappieVersion) => {
        set((state) => {
            state.mappieBot = botMappie;
        });
    },

    updatePrompt: (params: PromptUpdateParams) =>
        set((state) => {
            const updatedBot = updatePromptInMappieBot(state.mappieBot, params);

            if (params.isGlobal) {
                return {
                    mappieBot: updatedBot,
                    changesInBotVersion: true,
                    globalPromptUpdated: true,
                };
            }

            return {
                mappieBot: updatedBot,
                changesInBotVersion: true,
            };
        }),

    setGlobalPromptUpdated: (updated: boolean) =>
        set((state) => {
            state.globalPromptUpdated = updated;
        }),
});

/**
 * Creates custom module handles action handlers.
 */
export const createCustomModuleHandlesActions = (
    set: ImmerSet,
    _get: ImmerGet,
): Pick<
    EditableBotVersionSlice,
    'setCustomModuleNodeHandles' | 'clearCustomModuleNodeHandles'
> => ({
    setCustomModuleNodeHandles: (nodeId: string, handles: string[]) =>
        set((state) => ({
            customModuleNodeHandles: {
                ...state.customModuleNodeHandles,
                [nodeId]: handles,
            },
        })),

    clearCustomModuleNodeHandles: (nodeId: string) =>
        set((state) => {
            delete state.customModuleNodeHandles[nodeId];
        }),
});
